import type { ExistingPhoto } from "@/components/photos/PhotosEditField"

/**
 * Kinds of form a draft can belong to. Stored on every record so a future
 * draft list can group and label them, and so a schema change to one form
 * can't strand the others.
 */
export type DraftKind =
  | "observation.species"
  | "observation.collection"
  | "observation.existing-collection"
  | "site"
  | "geospatial"

/**
 * A staged photo. The bytes are held as an ArrayBuffer rather than a File or
 * Blob, matching how the offline HTTP cache stores response bodies: it is the
 * one binary form that structured-clones identically everywhere, including the
 * fake-indexeddb used in tests, which silently flattens a Blob to `{}`.
 * Rebuilt into a File on the way out — see `draftPhotoToStaged`.
 */
export type DraftPhoto = {
  id: string
  name: string
  type: string
  lastModified: number
  caption: string
  bytes: ArrayBuffer
}

export type FormDraft<TValues = unknown, TExtras = unknown> = {
  /**
   * The id of the entity this draft will become — the same uuid the form
   * minted up front and will submit under.
   *
   * Using it as the primary key means a draft is identified by the thing it
   * is, so the store holds as many concurrent drafts as the user has started
   * without any slot bookkeeping, and a restored draft keeps the identity its
   * staged photos were already recorded against.
   */
  id: string
  /**
   * Describes the *shape* of form this draft came from, so reopening that form
   * can offer the work back. Deliberately not unique: two new observations at
   * the same site are two drafts, and a future draft list shows both. Auto
   * recovery takes the most recently touched one.
   */
  resumeKey: string
  kind: DraftKind
  projectId: string
  /** Account that created the draft. Keeps shared-device drafts isolated. */
  ownerId: string
  /** Explicit saves persist until deletion; recovery records may be pruned. */
  savedByUser: boolean
  /** Short human-readable summary shown in the draft launcher. */
  label: string
  /** react-hook-form values, stored via structured clone (Dates survive). */
  values: TValues
  /** Form state that lives outside react-hook-form, e.g. the species picker. */
  extras: TExtras
  photos: Array<DraftPhoto>
  /**
   * For an edit form, the existing photos the user has chosen to keep, with
   * whatever caption edits they have made. Persisted separately from `photos`
   * because deletions and caption changes are edits to rows that already
   * exist — without this a restored edit would silently bring back photos the
   * user had removed. Null for forms with no existing photos to begin with.
   *
   * Null for forms with no existing photos to begin with.
   */
  existingPhotos: Array<ExistingPhoto> | null
  /**
   * Set while a camera or gallery capture is in flight. If it is still set
   * when a draft is restored, the app died mid-capture and the photo is gone —
   * the file the camera wrote is not reachable from the web layer.
   */
  captureInFlight: boolean
  createdAt: number
  updatedAt: number
}

/** Temporary recovery records older than this are cleaned up on startup. */
export const DRAFT_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000

/** Maximum number of explicitly saved drafts kept per user and project. */
export const DRAFTS_PER_PROJECT_LIMIT = 5

export type DraftSaveResult = "saved" | "limit-reached" | "failed"

type EditResumeLocation = {
  projectId: string
  mode: "edit"
  entityId: string
}

export type DraftResumeLocation =
  | (EditResumeLocation & { kind: DraftKind })
  | {
      kind: "site"
      projectId: string
      mode: "new"
      // Site types embedded in a project are identified by name; most have no id.
      siteTypeName: string
      duplicateFromId?: string
      plannedSiteId?: string
      assignmentId?: string
    }
  | {
      kind: "observation.species"
      projectId: string
      mode: "new"
      siteId?: string
      speciesId?: string
      duplicateFromId?: string
    }
  | {
      kind: "observation.collection"
      projectId: string
      mode: "new"
      siteId?: string
      tagCode?: string
      duplicateFromId?: string
    }
  | {
      kind: "observation.existing-collection"
      projectId: string
      mode: "new"
      siteId?: string
      collectionId?: string
      duplicateFromId?: string
    }
  | {
      kind: "geospatial"
      projectId: string
      // One per project, deliberately not keyed on the seed geometry: the
      // map's "New item" button seeds a live GPS fix that differs by a metre
      // or two every time, so keying on it would strand a mid-capture draft.
      mode: "new"
    }

const EMPTY_SCOPE_PART = "-"

function encodeScopePart(value?: string): string {
  return value ? encodeURIComponent(value) : EMPTY_SCOPE_PART
}

function decodeScopePart(value: string | undefined): string | undefined {
  if (!value || value === EMPTY_SCOPE_PART) return undefined
  return decodeURIComponent(value)
}

function isDraftKind(value: string): value is DraftKind {
  return [
    "site",
    "observation.species",
    "observation.collection",
    "observation.existing-collection",
    "geospatial",
  ].includes(value)
}

/** Builds the stable storage key for a typed form location. */
export function buildResumeKey(location: DraftResumeLocation): string {
  let scope: string

  if (location.mode === "edit") {
    scope = ["edit", encodeScopePart(location.entityId)].join(":")
  } else if (location.kind === "site") {
    scope = [
      "new",
      encodeScopePart(location.siteTypeName),
      encodeScopePart(location.duplicateFromId),
      encodeScopePart(location.plannedSiteId),
      encodeScopePart(location.assignmentId),
    ].join(":")
  } else if (location.kind === "observation.species") {
    scope = [
      "new",
      encodeScopePart(location.siteId),
      encodeScopePart(location.speciesId),
      encodeScopePart(location.duplicateFromId),
    ].join(":")
  } else if (location.kind === "observation.existing-collection") {
    scope = [
      "new",
      encodeScopePart(location.siteId),
      encodeScopePart(location.collectionId),
      encodeScopePart(location.duplicateFromId),
    ].join(":")
  } else if (location.kind === "geospatial") {
    scope = "new"
  } else {
    scope = [
      "new",
      encodeScopePart(location.siteId),
      encodeScopePart(location.tagCode),
      encodeScopePart(location.duplicateFromId),
    ].join(":")
  }

  return [location.kind, encodeURIComponent(location.projectId), scope].join(
    "|",
  )
}

/** Parses current and legacy positional keys into one checked representation. */
export function parseResumeKey(key: string): DraftResumeLocation | null {
  try {
    const [rawKind, rawProjectId, rawScope, ...extra] = key.split("|")
    if (
      !rawKind ||
      !isDraftKind(rawKind) ||
      !rawProjectId ||
      !rawScope ||
      extra.length > 0
    ) {
      return null
    }

    const projectId = decodeURIComponent(rawProjectId)
    const [mode, ...scope] = rawScope.split(":")
    if (mode === "edit") {
      const entityId = decodeScopePart(scope[0])
      return entityId ? { kind: rawKind, projectId, mode, entityId } : null
    }
    if (mode !== "new") return null

    if (rawKind === "site") {
      const siteTypeName = decodeScopePart(scope[0])
      return siteTypeName
        ? {
            kind: rawKind,
            projectId,
            mode,
            siteTypeName,
            duplicateFromId: decodeScopePart(scope[1]),
            plannedSiteId: decodeScopePart(scope[2]),
            assignmentId: decodeScopePart(scope[3]),
          }
        : null
    }

    const shared = {
      projectId,
      mode,
      siteId: decodeScopePart(scope[0]),
      duplicateFromId: decodeScopePart(scope[2]),
    } as const
    if (rawKind === "observation.species") {
      return {
        kind: rawKind,
        ...shared,
        speciesId: decodeScopePart(scope[1]),
      }
    }
    if (rawKind === "observation.existing-collection") {
      return {
        kind: rawKind,
        ...shared,
        collectionId: decodeScopePart(scope[1]),
      }
    }
    if (rawKind === "geospatial") {
      return { kind: rawKind, projectId, mode }
    }
    return {
      kind: rawKind,
      ...shared,
      tagCode: decodeScopePart(scope[1]),
    }
  } catch {
    return null
  }
}
